import { Box, Typography, Card, CardContent, Avatar, Button } from '@mui/material';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Pinterest as PinterestIcon,
  Email as EmailIcon,
  Article as BlogIcon,
  MusicNote as TikTokIcon,
  Upgrade as UpgradeIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { XIcon } from '../../components/icons/XIcon';
import { useUserAvailablePlatforms } from '../../hooks/usePlanEntitlements';
import { useAllPlatforms, useCurrentPlan } from '../../hooks/useCommon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/LoadingSpinner';
import { getPlatformDisplayName } from '../../utils/platformUtils';

interface PlatformSelectionProps {
  formData: any;
  setFormData: (data: any) => void;
  hasSocialMediaAccess: boolean;
  error?: boolean;
}

const PlatformSelection: React.FC<PlatformSelectionProps> = ({
  formData,
  setFormData,
  hasSocialMediaAccess,
  error = false,
}) => {
  const { t } = useTranslation('content');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: availablePlatforms, isLoading: loadingAvailable } = useUserAvailablePlatforms();
  const { data: allPlatforms, isLoading: loadingAll } = useAllPlatforms();
  const { data: currentPlan } = useCurrentPlan(user?.id);

  const isLoading = loadingAvailable || loadingAll;
  const availablePlatformIds = new Set((availablePlatforms || []).map((p: any) => p.id));
  const enabledPlatforms = (allPlatforms || []).filter((p: any) => availablePlatformIds.has(p.id));
  const disabledPlatforms = (allPlatforms || []).filter((p: any) => !availablePlatformIds.has(p.id));

  const isFreePlan = !currentPlan || 
    currentPlan.name === 'Free' || 
    currentPlan.name === 'Free Plan' || 
    currentPlan.name === 'FreeTest';

  const getPlatformIcon = (platformName: string) => {
    const iconProps = { sx: { fontSize: 24 } };
    
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return <InstagramIcon {...iconProps} />;
      case 'facebook':
        return <FacebookIcon {...iconProps} />;
      case 'twitter':
      case 'x':
        return <XIcon {...iconProps} />;
      case 'linkedin':
        return <LinkedInIcon {...iconProps} />;
      case 'youtube':
        return <YouTubeIcon {...iconProps} />;
      case 'pinterest':
        return <PinterestIcon {...iconProps} />;
      case 'email':
        return <EmailIcon {...iconProps} />;
      case 'blog':
        return <BlogIcon {...iconProps} />;
      case 'tiktok':
        return <TikTokIcon {...iconProps} />;
      default:
        return <BlogIcon {...iconProps} />;
    }
  };

  const getPlatformColor = (platformName: string, colorCode?: string) => {
    // Use backend color_code if provided, otherwise use defaults
    if (colorCode) return colorCode;
    
    switch (platformName.toLowerCase()) {
      case 'instagram':
        return '#E4405F';
      case 'facebook':
        return '#1877F2';
      case 'twitter':
      case 'x':
        return '#000000';
      case 'linkedin':
        return '#0A66C2';
      case 'youtube':
        return '#FF0000';
      case 'pinterest':
        return '#BD081C';
      case 'email':
        return '#6B7280';
      case 'blog':
        return '#10B981';
      case 'tiktok':
        return '#000000';
      default:
        return '#6B7280';
    }
  };

  const handlePlatformToggle = (platformName: string, isEnabled: boolean) => {
    if (!isEnabled) return; // Don't allow selection of locked platforms
    
    const currentPlatforms = formData.platforms || [];
    const newPlatforms = currentPlatforms.includes(platformName)
      ? currentPlatforms.filter((p: string) => p !== platformName)
      : [...currentPlatforms, platformName];
    
    setFormData((prev: any) => ({
      ...prev,
      platforms: newPlatforms,
    }));
  };

  // Always show platform selection, even if user has no social media access
  // This allows free users to see what platforms are available (locked)
  const allPlatformsSorted = [
    ...enabledPlatforms,
    ...disabledPlatforms,
  ];

  return (
    <Card sx={{ 
      border: '2px solid',
      borderColor: error ? 'error.main' : 'divider',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: error ? 'rgba(211, 47, 47, 0.02)' : 'inherit',
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2, color: error ? 'error.main' : 'inherit' }}>
          {t('generation.selectPlatform')} {error && '*'}
        </Typography>
        <Typography variant="body2" color={error ? 'error.main' : 'text.secondary'} sx={{ mb: 3 }}>
          {t('generation.selectPlatformDesc')}
          {error && ` (${t('generation.platformRequired')})`}
        </Typography>

        {isLoading ? (
          <LoadingSpinner message={t('generation.loadingPlatforms')} />
        ) : (
          <Box>
            {/* Platform Icons Grid */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5,
              mb: disabledPlatforms.length > 0 && isFreePlan ? 2 : 0,
            }}>
              {allPlatformsSorted.map((platform: any) => {
                const isEnabled = availablePlatformIds.has(platform.id);
                const isSelected = (formData.platforms || []).includes(platform.name);
                const platformColor = getPlatformColor(platform.name, platform.color_code);
                
                return (
                  <Box
                    key={platform.id || platform.name}
                    sx={{
                      position: 'relative',
                      cursor: isEnabled ? 'pointer' : 'default',
                    }}
                    onClick={() => handlePlatformToggle(platform.name, isEnabled)}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: isSelected 
                          ? platformColor 
                          : (isEnabled ? 'grey.300' : 'grey.400'),
                        width: 50,
                        height: 50,
                        transition: 'all 0.2s ease',
                        opacity: isEnabled ? 1 : 0.6,
                        filter: isEnabled ? 'none' : 'grayscale(100%)',
                        border: isSelected ? '3px solid #6C63FF' : 'none',
                        boxShadow: isSelected 
                          ? `0 0 0 2px rgba(108, 99, 255, 0.3), 0 4px 12px ${platformColor}40` 
                          : 'none',
                        '&:hover': isEnabled ? {
                          transform: 'scale(1.1)',
                          boxShadow: isSelected 
                            ? `0 0 0 2px rgba(108, 99, 255, 0.3), 0 4px 12px ${platformColor}40`
                            : '0 4px 12px rgba(0,0,0,0.15)',
                        } : {},
                      }}
                      title={`${getPlatformDisplayName(platform.display_name || platform.name)}${isEnabled ? (isSelected ? ` (${t('generation.clickToDeselect')})` : ` (${t('generation.clickToSelect')})`) : ` (${t('generation.upgradeToUnlockPlatform')})`}`}
                    >
                      {getPlatformIcon(platform.name)}
                    </Avatar>
                    {/* Lock icon badge for disabled platforms */}
                    {!isEnabled && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          backgroundColor: '#6C63FF',
                          borderRadius: '50%',
                          width: 22,
                          height: 22,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid white',
                          boxShadow: '0 2px 6px rgba(108, 99, 255, 0.4)',
                          zIndex: 1,
                        }}
                      >
                        <LockIcon sx={{ fontSize: 14, color: 'white' }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            
            {/* Upgrade CTA - Show if there are disabled platforms */}
            {disabledPlatforms.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                pt: 2,
                mt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => navigate('/billing')}
                  startIcon={<UpgradeIcon />}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
                    },
                  }}
                >
                  {isFreePlan ? t('generation.upgradeToUnlock') : t('generation.unlockMorePlatforms')}
                </Button>
              </Box>
            )}

          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatformSelection;
